import { 
	Typography, 
	Box,
	Fab,
	Divider
} from '@mui/material';
import { Dialog } from '@mui/material';
import React, { useEffect, useState, useCallback } from 'react';
import { PlanImageItem } from '../types';
import { DialogHeader } from './DialogHeader';
import placeholderImage from '../assets/product-placeholder.png';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

interface PlanImageDialogFullScreenProps {
    open: boolean;
    onClose: () => void;
    images: PlanImageItem[];
    title?: string;
    initialIndex?: number;
}

export const PlanImageDialogFullScreen = ({ open, onClose, images, title, initialIndex = 0 }: PlanImageDialogFullScreenProps) => {
	const [currentPanel, setCurrentPanel] = useState(initialIndex);

	useEffect(() => {
		if (open) {
			setCurrentPanel(initialIndex);
		}
	}, [open, initialIndex]);

	const totalPanels = images.length || 1;

	const handleKeyDown = useCallback((event: KeyboardEvent) => {
		if (!open) return;
		switch (event.key) {
		case 'ArrowLeft':
			event.preventDefault();
			setCurrentPanel(prev => Math.max(0, prev - 1));
			break;
		case 'ArrowRight':
			event.preventDefault();
			setCurrentPanel(prev => Math.min(totalPanels - 1, prev + 1));
			break;
		case 'Escape':
			event.preventDefault();
			onClose();
			break;
		}
	}, [open, totalPanels, onClose]);

	useEffect(() => {
		document.addEventListener('keydown', handleKeyDown);
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
		};
	}, [handleKeyDown]);

	const goToPrevious = () => setCurrentPanel(prev => Math.max(0, prev - 1));
	const goToNext = () => setCurrentPanel(prev => Math.min(totalPanels - 1, prev + 1));

	const renderImagePanel = (image: PlanImageItem, index: number) => (
		<Box sx={{ 
			width: '100%', 
			height: '100%', 
			display: 'flex', 
			alignItems: 'center', 
			justifyContent: 'center',
			position: 'relative',
			backgroundColor: 'black'
		}}>
			<img 
				src={image.imgUrl || placeholderImage} 
				alt={image.title || `Plan image ${index + 1}`}
				style={{
					maxWidth: '100%',
					maxHeight: '100%',
					objectFit: 'contain'
				}}
			/>
			{image.title && (
				<Box sx={{
					position: 'absolute',
					bottom: 20,
					left: 20,
					right: 20,
					backgroundColor: 'rgba(0,0,0,0.7)',
					color: 'white',
					p: 2,
					borderRadius: 1
				}}>
					<Typography variant='body1'>{image.title}</Typography>
				</Box>
			)}
		</Box>
	);

	return (
		<Dialog open={open} onClose={onClose} fullScreen>
			<DialogHeader title={title ?? ''} onClose={onClose} />
			<Divider />
			<Box sx={{ 
				position: 'relative', 
				height: '100%', 
				overflow: 'hidden'
			}}>
				<Box sx={{ 
					display: 'flex', 
					width: `${Math.max(totalPanels, 1) * 100}vw`, 
					height: '100%',
					transform: `translateX(-${currentPanel * 100}vw)`,
					transition: 'transform 0.3s ease-in-out'
				}}>
					{(images.length ? images : [{ id: -1, imgUrl: '', title: '', rank: 0 }]).map((image, index) => (
						<Box key={index} sx={{ width: '100vw', height: '100%', flexShrink: 0 }}>
							{renderImagePanel(image as PlanImageItem, index)}
						</Box>
					))}
				</Box>

				{currentPanel > 0 && (
					<Fab size="small" onClick={goToPrevious} sx={{
						position: 'absolute',
						left: 16,
						top: '50%',
						transform: 'translateY(-50%)',
						backgroundColor: 'rgba(255,255,255,0.9)',
						'&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
					}}>
						<ChevronLeftIcon />
					</Fab>
				)}

				{currentPanel < totalPanels - 1 && (
					<Fab size="small" onClick={goToNext} sx={{
						position: 'absolute',
						right: 16,
						top: '50%',
						transform: 'translateY(-50%)',
						backgroundColor: 'rgba(255,255,255,0.9)',
						'&:hover': { backgroundColor: 'rgba(255,255,255,1)' }
					}}>
						<ChevronRightIcon />
					</Fab>
				)}

				{totalPanels > 1 && (
					<Box sx={{
						position: 'absolute',
						bottom: 20,
						left: '50%',
						transform: 'translateX(-50%)',
						display: 'flex',
						gap: 1
					}}>
						{Array.from({ length: totalPanels }, (_, index) => (
							<Box
								key={index}
								sx={{
									width: 8,
									height: 8,
									borderRadius: '50%',
									backgroundColor: currentPanel === index ? 'white' : 'rgba(255,255,255,0.5)',
									cursor: 'pointer',
									transition: 'background-color 0.2s'
								}}
								onClick={() => setCurrentPanel(index)}
							/>
						))}
					</Box>
				)}
			</Box>
		</Dialog>
	);
};
